import type { ReactElement } from "react";
import type { MenuTab } from "../store.js";

/**
 * The menu's glyphs: one 24-unit outline per tab plus a few for controls.
 * Inline SVG so they take `currentColor` and scale with the UI. Stroke only,
 * 1.75 wide, round joins — the same pen for every icon.
 */

export interface IconProps {
  /** Rendered size in px. */
  size?: number;
  className?: string;
}

function Svg(props: IconProps & { children: ReactElement | ReactElement[] }): ReactElement {
  return (
    <svg
      className={props.className}
      width={props.size ?? 16}
      height={props.size ?? 16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {props.children}
    </svg>
  );
}

/** Gear: a rucksack. */
export function IconGear(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M7 9a5 5 0 0 1 10 0v11H7z" />
      <path d="M9 5.5V4.5a3 3 0 0 1 6 0v1" />
      <path d="M7 14h10M10 17h4" />
    </Svg>
  );
}

/** Profile: a person. */
export function IconProfile(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </Svg>
  );
}

/** Traders: a shop awning. */
export function IconTraders(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M4 10 5.2 5h13.6L20 10" />
      <path d="M4 10c0 1.4 1.1 2.5 2.5 2.5S9 11.4 9 10c0 1.4 1.1 2.5 2.5 2.5S14 11.4 14 10c0 1.4 1.1 2.5 2.5 2.5S20 11.4 20 10" />
      <path d="M5.5 12.5V20h13v-7.5M10 20v-4.5h4V20" />
    </Svg>
  );
}

/** Market: a list with rows. */
export function IconMarket(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M8 6h12M8 12h12M8 18h12" />
      <path d="M4 6h.01M4 12h.01M4 18h.01" strokeWidth={2.5} />
    </Svg>
  );
}

/** Hideout: a house. */
export function IconHideout(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M3.5 11 12 4l8.5 7" />
      <path d="M6 9.5V20h12V9.5" />
      <path d="M10 20v-5h4v5" />
    </Svg>
  );
}

/** Quests: a flag. */
export function IconQuests(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M5 21V4" />
      <path d="M5 4h11l-1.6 3.5L16 11H5" />
    </Svg>
  );
}

/** Friends: two people. */
export function IconFriends(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <circle cx="9" cy="8.5" r="3" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <path d="M15.5 6a3 3 0 0 1 0 5.5M17 14.5a5 5 0 0 1 3.5 4.5" />
    </Svg>
  );
}

/** Settings: a cog. */
export function IconSettings(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v2.4M12 19.1v2.4M2.5 12h2.4M19.1 12h2.4M5.3 5.3l1.7 1.7M17 17l1.7 1.7M5.3 18.7 7 17M17 7l1.7-1.7" />
    </Svg>
  );
}

/** Search: a magnifier. */
export function IconSearch(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <circle cx="11" cy="11" r="6" />
      <path d="m20 20-4.5-4.5" />
    </Svg>
  );
}

/** Close: a cross. */
export function IconClose(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="m6 6 12 12M18 6 6 18" />
    </Svg>
  );
}

/** Sign out: a door with an arrow. */
export function IconSignOut(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M10 4H5.5A1.5 1.5 0 0 0 4 5.5v13A1.5 1.5 0 0 0 5.5 20H10" />
      <path d="M14 8l4 4-4 4M18 12H9" />
    </Svg>
  );
}

/** Deploy: a chevron-right arrow. */
export function IconDeploy(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M5 12h13M13 6l6 6-6 6" />
    </Svg>
  );
}

/** Coins: the wallet glyph. */
export function IconEuro(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M17.5 6.5A7 7 0 0 0 6.6 9.5M17.5 17.5A7 7 0 0 1 6.6 14.5" />
      <path d="M4 10.5h9M4 13.5h9" />
    </Svg>
  );
}

/** Check mark. */
export function IconCheck(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </Svg>
  );
}

/** Padlock: a shut gate. */
export function IconLock(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <rect x="5" y="10.5" width="14" height="10" rx="2" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
    </Svg>
  );
}

/** Crown: the squad leader. */
export function IconCrown(p: IconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="m4 17 1-9 4.5 4L12 6l2.5 6L19 8l1 9z" />
      <path d="M4 20h16" />
    </Svg>
  );
}

/** The tab strip's icon for each `MenuTab`. */
export const TAB_ICON: Record<MenuTab, (p: IconProps) => ReactElement> = {
  gear: IconGear,
  profile: IconProfile,
  traders: IconTraders,
  market: IconMarket,
  hideout: IconHideout,
  quests: IconQuests,
  friends: IconFriends,
  settings: IconSettings,
};
